<template>
  <div class="jwl-show-more">
    <div class="jwl-show-more__text-container" :class="hasMoreClass" ref="showMoreContainer">
      <div class="jwl-show-more__text" ref="showMoreText" dir="auto">
        <div class="jwl-show-more__asset" v-if="jwlImage || jwlVideo">
          <picture v-if="jwlImage && !jwlVideo">
            <img :src="jwlImage.url" />
          </picture>

          <JwlVideoPlayer v-else :video-file="jwlVideo" />
        </div>

        <div class="jwl-show-more__raw-text" v-html="cleanedText" />
      </div>
    </div>

    <div class="jwl-show-more__button" v-if="hasMore && !disabled" @click="toggleShowMore">
      {{t(showMore ? 'general.showLess' : 'general.showMore')}}
    </div>
  </div>
</template>

<script setup>
  const props = defineProps({
    disabled: Boolean,
    jwlText: String,
    jwlImage: Object,
    jwlVideo: Object,
  });

  const { t } = useI18n();

  const showMoreContainer = ref();
  const showMoreText = ref();

  const hasMore = ref(true);
  const showMore = ref(false);

  const checkText = () => {
    hasMore.value = true;
    showMore.value = false;
    calculateHeights();
  };

  watch(() => props.jwlText, () => {
    checkText();
  });

  const calculateHeights = () => {
    nextTick(() => {
      const descContainer = showMoreContainer.value;
      const descText = showMoreText.value;
      if (descContainer && descText) {
        const outerHeight = descContainer.getBoundingClientRect().height;
        const innerHeight = descText.getBoundingClientRect().height;

        if (outerHeight >= innerHeight) {
          hasMore.value = false;
        }
      }
    });
  }

  const toggleShowMore = () => {
    showMore.value = !showMore.value;
  };

  const hasMoreClass = computed(() => ({
    'jwl-show-more__text-container--open': hasMore.value && showMore.value,
    'jwl-show-more__text-container--has-more': hasMore.value && !showMore.value,
    'jwl-show-more__text-container--has-not-more': !hasMore.value,
  }));

  const videoPosterUrl = computed(() => {
    if (props.jwlVideo && props.jwlVideo.poster) {
      return props.jwlVideo.poster;
    }

    if (props.jwlImage) {
      return props.jwlImage.url;
    }

    return '';
  });

  const cleanedText = computed(() => props.jwlText.replace('&nbsp;', ' '));

  onMounted(() => {
    if (props.disabled) {
      hasMore.value = true;
      showMore.value = true;
    }
  });
</script>

<style lang="scss">
@use "@base/styles/mixins";
@use "@base/styles/variables";

.jwl-show-more {
  display: flex;
  position: relative;

  &__text-container {
    font-weight: variables.$font-weight-light;
    margin-bottom: .5rem;
    max-height: 10rem;
    overflow: hidden;

    &--has-not-more {
      max-height: max-content;
    }

    &--open {
      margin-bottom: 1.5rem;
      max-height: max-content;
    }

    &--has-more {
      position: relative;

      &::after {
        background: linear-gradient(rgba(255, 255, 255, 0), var(--color-white) 70%, var(--color-white));
        bottom: 0;
        content: '';
        display: block;
        height: 3.5rem;
        left: 0;
        position: absolute;
        width: 100%;
      }
    }
  }

  &__text {
    display: inline;

    h1, h2, h3, h4, h5, h6 {
      margin-top: 0;
      padding-top: 0;
    }
  }

  &__asset {
    @include mixins.ltrtl(margin-left, margin-right, 1rem);

    float: right;
    margin-bottom: 1.5rem;
    width: 50%;

    img,
    video {
      border-radius: var(--border-radius);
      object-fit: cover;
      width: 100%;
    }

    @include mixins.mq(variables.$mq-phone) {
      @include mixins.ltrtl(margin-left, margin-right, 0);

      margin-bottom: 0;
      width: 100%;
    }
  }

  &__button {
    bottom: 0;
    color: var(--color-blue);
    cursor: pointer;
    position: absolute;
    text-decoration: underline;
    transition: color .25s;

    &:hover {
      color: var(--concentration-color, #{darken(variables.$blue, 10%)});
    }
  }
}
</style>
